import { useState, useEffect, useRef } from 'react'
import styles from './OurTeam.module.css'

const statsData = [
  {
    label: 'Years Experience',
    target: 14,
    icon: (
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 8v4l3 3" />
        <circle cx="12" cy="12" r="10" />
      </svg>
    ),
  },
  {
    label: 'Team Members',
    target: 80,
    icon: (
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
  },
  {
    label: 'Satisfied Clients',
    target: 100,
    icon: (
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
      </svg>
    ),
  },
  {
    label: 'Projects Done',
    target: 350,
    icon: (
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
        <polyline points="22 4 12 14.01 9 11.01" />
      </svg>
    ),
  },
]

const easeOutExpo = (t) => (t === 1 ? 1 : 1 - Math.pow(2, -10 * t))

const useCountUp = (target, duration = 2200, shouldStart) => {
  const [count, setCount] = useState(0)
  const rafRef = useRef(null)

  useEffect(() => {
    if (!shouldStart) return
    const startTime = performance.now()

    const tick = (now) => {
      const elapsed = now - startTime
      const progress = Math.min(elapsed / duration, 1)
      const eased = easeOutExpo(progress)
      setCount(Math.round(eased * target))

      if (progress < 1) {
        rafRef.current = requestAnimationFrame(tick)
      }
    }

    rafRef.current = requestAnimationFrame(tick)
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
    }
  }, [target, duration, shouldStart])

  return count
}

const StatItem = ({ icon, label, target, shouldStart, index }) => {
  const count = useCountUp(target, 2200, shouldStart)

  return (
    <div
      className={styles.statCard}
      style={{ transitionDelay: `${index * 120}ms` }}
    >
      <div className={styles.statIconWrap}>{icon}</div>
      <div className={styles.statValue}>
        <span className={styles.statNumber}>{count}</span>
        <span className={styles.statPlus}>+</span>
      </div>
      <p className={styles.statLabel}>{label}</p>
    </div>
  )
}

const OurTeam = () => {
  const [isVisible, setIsVisible] = useState(false)
  const sectionRef = useRef(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
          observer.disconnect()
        }
      },
      { threshold: 0.2 }
    )
    if (sectionRef.current) {
      observer.observe(sectionRef.current)
    }
    return () => observer.disconnect()
  }, [])

  return (
    <section
      className={`${styles.section} ${isVisible ? styles.sectionVisible : ''}`}
      ref={sectionRef}
    >
      <div className={styles.inner}>
        <div className={styles.statsHeader}>
          <span className={styles.sectionLabel}>By the Numbers</span>
          <h2 className={styles.sectionTitle}>
            The results speak for themselves
          </h2>
        </div>
        <div className={styles.statsGrid}>
          {statsData.map((stat, index) => (
            <StatItem
              key={stat.label}
              icon={stat.icon}
              label={stat.label}
              target={stat.target}
              shouldStart={isVisible}
              index={index}
            />
          ))}
        </div>
      </div>
    </section>
  )
}

export default OurTeam
